import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';

const Messages = () => {
  const { user } = useContext(AuthContext);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const messagePollingRef = useRef(null);
  const conversationPollingRef = useRef(null);

  useEffect(() => {
    if (user) {
      fetchConversations();
      // Start polling for new conversations every 10 seconds
      startConversationPolling();
    }
    return () => {
      stopConversationPolling();
    };
  }, [user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Start message polling when active conversation changes
    if (activeConversation) {
      startMessagePolling();
    } else {
      stopMessagePolling();
    }
    
    return () => {
      stopMessagePolling();
    };
  }, [activeConversation]);

  useEffect(() => {
    return () => {
      // Cleanup WebSocket connection and polling
      if (wsRef.current) {
        wsRef.current.close();
      }
      stopMessagePolling();
      stopConversationPolling();
    };
  }, []);

  // Auto-polling functions
  const startMessagePolling = () => {
    stopMessagePolling(); // Clear any existing polling
    if (activeConversation) {
      messagePollingRef.current = setInterval(() => {
        fetchMessages(activeConversation.id, true); // Silent fetch for polling
      }, 3000); // Poll every 3 seconds
    }
  };

  const stopMessagePolling = () => {
    if (messagePollingRef.current) {
      clearInterval(messagePollingRef.current);
      messagePollingRef.current = null;
    }
  };

  const startConversationPolling = () => {
    stopConversationPolling(); // Clear any existing polling
    conversationPollingRef.current = setInterval(() => {
      fetchConversations(true); // Silent fetch for polling
    }, 10000); // Poll every 10 seconds for new conversations
  };

  const stopConversationPolling = () => {
    if (conversationPollingRef.current) {
      clearInterval(conversationPollingRef.current);
      conversationPollingRef.current = null;
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async (silent = false) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      if (!silent) {
        console.error('Error fetching conversations:', error);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const selectConversation = async (conversation) => {
    if (activeConversation?.id === conversation.id) return;
    
    // Close existing WebSocket connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    setActiveConversation(conversation);
    await fetchMessages(conversation.id);
    setupWebSocket(conversation.id);
    // Polling will automatically start via useEffect when activeConversation changes
  };

  const fetchMessages = async (roomId, silent = false) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/messages/${roomId}/history`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const newMessages = response.data || [];
      
      // Only update if messages have actually changed (to prevent unnecessary re-renders)
      setMessages(prevMessages => {
        if (prevMessages.length !== newMessages.length) {
          return newMessages;
        }
        // Check if last message is different (simple check for new messages)
        if (prevMessages.length > 0 && newMessages.length > 0) {
          const lastPrevMessage = prevMessages[prevMessages.length - 1];
          const lastNewMessage = newMessages[newMessages.length - 1];
          if (lastPrevMessage.id !== lastNewMessage.id) {
            return newMessages;
          }
        }
        return prevMessages;
      });
    } catch (error) {
      if (!silent) {
        console.error('Error fetching messages:', error);
      }
    }
  };

  const setupWebSocket = (roomId) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = backendUrl.replace('http://', 'ws://').replace('https://', 'wss://');
    const token = localStorage.getItem('token');
    
    wsRef.current = new WebSocket(`${wsUrl}/api/messages/ws/${roomId}?token=${token}`);
    
    wsRef.current.onopen = () => {
      console.log('WebSocket connected for room:', roomId);
    };
    
    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'new_message') {
        setMessages(prev => [...prev, data.message]);
        // Update conversations list with new last message
        fetchConversations();
      }
    };
    
    wsRef.current.onclose = () => {
      console.log('WebSocket disconnected');
    };
    
    wsRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConversation || sendingMessage) return;
    
    setSendingMessage(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await axios.post(`${backendUrl}/api/messages/${activeConversation.id}/send`, {
        content: newMessage.trim(),
        message_type: 'text'
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setNewMessage('');
      
      // Immediately fetch messages to show the sent message
      await fetchMessages(activeConversation.id);
      
      // Also update conversations list to show latest message
      fetchConversations(true);
      
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays <= 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const getConversationName = (conversation) => {
    if (conversation.room_type === 'direct' && conversation.other_user) {
      return conversation.other_user.full_name;
    }
    return conversation.name;
  };

  const getConversationAvatar = (conversation) => {
    if (conversation.room_type === 'direct' && conversation.other_user) {
      return conversation.other_user.full_name.charAt(0);
    }
    return conversation.name.charAt(0);
  };

  const filteredConversations = conversations.filter(conv =>
    getConversationName(conv).toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto h-screen flex">
        {/* Sidebar - Conversations List */}
        <div className="w-1/3 bg-white border-r border-gray-200 flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-bold text-gray-900">💬 Messages</h1>
              {/* Polling indicator */}
              {activeConversation && (
                <div className="flex items-center text-sm text-gray-500">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></div>
                  <span>Auto-refresh</span>
                </div>
              )}
            </div>
            
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
            </div>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {filteredConversations.length === 0 ? (
              <div className="p-6 text-center">
                <div className="text-gray-400 mb-2">💬</div>
                <p className="text-gray-500">No conversations yet</p>
                <p className="text-sm text-gray-400 mt-1">Start messaging your connections!</p>
              </div>
            ) : (
              filteredConversations.map((conversation) => (
                <div
                  key={conversation.id}
                  onClick={() => selectConversation(conversation)}
                  className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                    activeConversation?.id === conversation.id ? 'bg-teal-50 border-r-4 border-r-teal-500' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {/* Avatar */}
                    <div className="w-12 h-12 bg-teal-500 text-white rounded-full flex items-center justify-center font-semibold">
                      {getConversationAvatar(conversation)}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {getConversationName(conversation)}
                        </h3>
                        {conversation.last_message && (
                          <span className="text-xs text-gray-500">
                            {formatTime(conversation.last_message.created_at)}
                          </span>
                        )}
                      </div>
                      
                      {conversation.last_message ? (
                        <p className="text-sm text-gray-600 truncate">
                          {conversation.last_message.sender_id === user.id ? 'You: ' : ''}
                          {conversation.last_message.content}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-400 italic">No messages yet</p>
                      )}
                      
                      {conversation.room_type === 'direct' && conversation.other_user?.company_name && (
                        <p className="text-xs text-gray-400 mt-1">{conversation.other_user.company_name}</p>
                      )}
                    </div>
                    
                    {/* Unread indicator */}
                    {conversation.unread_count > 0 && (
                      <div className="w-5 h-5 bg-teal-500 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                        {conversation.unread_count > 9 ? '9+' : conversation.unread_count}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col">
          {activeConversation ? (
            <>
              {/* Chat Header */}
              <div className="bg-white border-b border-gray-200 p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-teal-500 text-white rounded-full flex items-center justify-center font-semibold">
                    {getConversationAvatar(activeConversation)}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {getConversationName(activeConversation)}
                    </h2>
                    {activeConversation.room_type === 'direct' && activeConversation.other_user && (
                      <p className="text-sm text-gray-500">
                        {activeConversation.other_user.company_name && `${activeConversation.other_user.company_name} • `}
                        {activeConversation.other_user.location}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((message, index) => {
                  const isOwnMessage = message.sender_id === user.id;
                  const showAvatar = index === 0 || messages[index - 1].sender_id !== message.sender_id;
                  
                  return (
                    <div
                      key={message.id}
                      className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`flex items-end space-x-2 max-w-xs lg:max-w-md ${isOwnMessage ? 'flex-row-reverse space-x-reverse' : ''}`}>
                        {/* Avatar */}
                        {!isOwnMessage && (
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${showAvatar ? 'bg-gray-300 text-gray-700' : 'opacity-0'}`}>
                            {showAvatar && (message.sender?.full_name?.charAt(0) || '?')}
                          </div>
                        )}
                        
                        {/* Message Bubble */}
                        <div
                          className={`px-4 py-2 rounded-lg ${
                            isOwnMessage
                              ? 'bg-teal-500 text-white rounded-br-none'
                              : 'bg-white text-gray-900 border border-gray-200 rounded-bl-none'
                          }`}
                        >
                          {/* Sender name for received messages */}
                          {!isOwnMessage && showAvatar && (
                            <p className="text-xs text-gray-500 mb-1">{message.sender?.full_name}</p>
                          )}
                          
                          <p className="text-sm">{message.content}</p>
                          
                          {/* Timestamp */}
                          <p className={`text-xs mt-1 ${isOwnMessage ? 'text-teal-100' : 'text-gray-500'}`}>
                            {new Date(message.created_at).toLocaleTimeString([], { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="bg-white border-t border-gray-200 p-4">
                <div className="flex items-end space-x-2">
                  <div className="flex-1">
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your message..."
                      rows={1}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      style={{ minHeight: '44px', maxHeight: '120px' }}
                    />
                  </div>
                  <button
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                    className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {sendingMessage ? '⏳' : '📤'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            /* No conversation selected */
            <div className="flex-1 flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <div className="text-6xl mb-4">💬</div>
                <h2 className="text-xl font-semibold text-gray-700 mb-2">Select a conversation</h2>
                <p className="text-gray-500">Choose a conversation from the sidebar to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;